import type { ReportFilters } from "@/app/features/reports/types/report-data";
import { CollapseAllIcon, ExpandAllIcon } from "@/components/raytd/icons";
import OptionToggle from "@/components/raytd/option-toggle";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Maximize2Icon, Minimize2Icon } from "lucide-react";
import { ReportGroupModes, ReportViewModes } from "../types";
import useReportViewerContext, { useReportViewContext } from "./report-viewer-context";
import { useReportGroupingContext } from "../contexts/grouping-context";
import { FilterTypes } from "../types/filters";
import { useMemo } from "react";
import TooltipButton from "@/components/raytd/tooltip-button";
import { ReportExportDialog, type ExportScope } from "@/app/features/reports/published/components/report-export-dialog";


const Divider = () => (
    <div className="h-full w-px bg-zinc-200" aria-hidden="true" />
);

interface ReportToolbarProps {
    setViewMode: (viewMode: ReportViewModes) => void;
    filters: ReportFilters;
    onExportExcel?: (params: { scope: ExportScope }) => Promise<void>;
    onExportPdf?: (params: { scope: ExportScope; mode: 'full' | 'results_only' }) => Promise<void>;
    onExportWord?: (params: { scope: ExportScope; groupBy: 'location' | 'test' | 'element_group' }) => Promise<void>;
    isExportingExcel?: boolean;
    isExportingPdf?: boolean;
    isExportingWord?: boolean;
    canExportExcel?: boolean;
    canExportPdf?: boolean;
    canExportWord?: boolean;
    defaultScope?: ExportScope;
}

const GROUP_OPTIONS = [
    { description: 'Location', value: 'location' },
    { description: 'Element', value: 'element' },
    { description: 'Test', value: 'test' },
    { description: 'Rating', value: 'rating' },
    { description: 'Category', value: 'category' }
] as const;

const ReportToolbar: React.FC<ReportToolbarProps> = ({
    filters,
    onExportExcel,
    onExportPdf,
    onExportWord,
    isExportingExcel = false,
    isExportingPdf = false,
    isExportingWord = false,
    canExportExcel = false,
    canExportPdf = false,
    canExportWord = false,
    defaultScope = 'all',
}) => {
    const { groupMode, setGroupMode, isSdaFormat, groupModeLocked } = useReportViewContext();
    const { readOnly, showTestEditor } = useReportViewerContext();
    const {
        expandAll,
        collapseAll,
        expandAllTiles,
        collapseAllTiles
    } = useReportGroupingContext();

    const handleGroupModeChange = (value: string) => {
        setGroupMode(value as ReportGroupModes);
    };

    // Determine the exposed facet based on groupMode
    const exposedFacetForSection = useMemo((): FilterTypes | undefined => {
        switch (groupMode) {
            case 'location': return 'space';
            case 'element': return 'element';
            case 'test': return 'testSuite';
            case 'rating': return 'rating';
            case 'category': return 'testSuite';
            default: return undefined;
        }
    }, [groupMode]);

    // TODO: Re-enable export dialog in production once export functionality is finalized.
    const shouldShowExportDialog = !import.meta.env.PROD && (canExportExcel || canExportPdf || canExportWord) && onExportWord

    return (
        <div
            className={cn(
                "flex flex-row items-start",
                "gap-4 mb-2 pb-2",
                "border-b border-zinc-200",
                "transition-all duration-200"
            )}
        >
            {/* SDA reports are forced into Category grouping (raytd/web QA 108.03, item 2) —
                the selector itself doesn't render, rather than just defaulting to Category.
                An SDA-eligible Inspection's Category view (raytd/web QA 108.04) is a switchable
                default, not a lock, so it keeps groupModeLocked false and the selector stays. */}
            {!groupModeLocked && (
                <OptionToggle
                    options={GROUP_OPTIONS}
                    value={groupMode}
                    onChange={handleGroupModeChange}
                />
            )}

            {/* Expand/Collapse also drives the sticky-header pinning state, not just tile
                layout — hiding it for SDA (item 3) broke that for SDA views too, so it renders
                unconditionally regardless of isSdaFormat (raytd/web QA 108.03, item 11). */}
            <div className="flex gap-2">
                <TooltipButton
                    tooltip="Expand All"
                    onClick={expandAll}
                    icon={ExpandAllIcon}
                />
                <TooltipButton
                    tooltip="Collapse All"
                    onClick={collapseAll}
                    icon={CollapseAllIcon}
                />
                {/* SDA's compact table has no separate collapsed tile state — QA initially
                    flagged these as "collapse does nothing" (108.03 item 16, 108.04 item 2),
                    then corrected that the fix is to hide them for SDA outright, not make them
                    do something meaningful. */}
                {!isSdaFormat && (
                    <>
                        <Divider />
                        <TooltipButton
                            tooltip="Expand All Tiles"
                            onClick={expandAllTiles}
                            icon={Maximize2Icon}
                        />
                        <TooltipButton
                            tooltip="Collapse All Tiles"
                            onClick={collapseAllTiles}
                            icon={Minimize2Icon}
                        />
                    </>
                )}
            </div>

            {shouldShowExportDialog && (
                <ReportExportDialog
                    onExportExcel={onExportExcel ?? (async () => Promise.resolve())}
                    onExportPdf={onExportPdf ?? (async () => Promise.resolve())}
                    onExportWord={onExportWord}
                    isExportingExcel={isExportingExcel}
                    isExportingPdf={isExportingPdf}
                    isExportingWord={isExportingWord}
                    canExportExcel={canExportExcel}
                    canExportPdf={canExportPdf}
                    canExportWord={canExportWord}
                    defaultScope={defaultScope}
                    singleWordFormat={isSdaFormat}
                />
            )}

            <div className="flex-1" />
            {!readOnly && <Button variant='default' onClick={() => showTestEditor(null)}>New Test</Button>}
        </div>
    );
};

export default ReportToolbar;